import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from 'react-router-dom';
import {
	List,
	ListItem,
	ListItemButton,
	ListItemIcon,
	ListItemText,
	Collapse,
	Tooltip,
} from '@mui/material';
import { Box } from "@mui/system";
import {
	HomeOutlined as HomeIcon,
	GroupsOutlined as GroupsIcon,
	BookOutlined as BookIcon,
	QuestionAnswerOutlined as QuestionAnswerIcon,
	AssignmentOutlined as AssignmentIcon,
	NoteAddOutlined as NoteAddIcon,
	CheckCircleOutlined as CheckCircleIcon,
	FeedbackOutlined as FeedbackIcon,
	ForumOutlined as ForumIcon,
	RateReviewOutlined as RateReviewIcon,
	LowPriorityOutlined as LowPriorityIcon,
	LocalActivityOutlined as LocalActivityIcon,
	FactCheckOutlined as FactCheckIcon,
	PeopleOutlined as PeopleIcon,
	HandshakeOutlined as EngagementIcon,
	SettingsOutlined as SettingsIcon,
	HistoryOutlined as HistoryIcon,
	ExpandLessOutlined,
	ExpandMoreOutlined,
} from '@mui/icons-material';
import { Typography } from "antd";
import { useAuth } from "../AuthContext/AuthContext";

const ProSideBar = (props) => {
	const navigate = useNavigate();
	const location = useLocation();
	const { canAccessModule } = useAuth();
	const [collapsed, setCollapsed] = useState(true);
	const [feedbackOpen, setFeedbackOpen] = useState(false);

	useEffect(() => {
		setCollapsed(!props.checked);
	}, [props.checked]);

	const getItem = (label, key, icon, tooltip, children = null, disabled = false) => {
		return {
			key,
			icon,
			tooltip,
			label,
			children,
			disabled,
		};
	};

	const feedArray = [
		getItem('Feedback', '/feedback', <FeedbackIcon fontSize="small" />, null, null, !canAccessModule("FEEDBACK", "read")),
		getItem('Interactions', '/interactions', <ForumIcon fontSize="small" />, null, null, !canAccessModule("INTERACTIONS", "read")),
		getItem('Lesson Activities', '/activities', <LocalActivityIcon fontSize="small" />, null, null, !canAccessModule("LESSON_ACTIVITIES", "read")),
		getItem('Community Priorities', '/priorities', <LowPriorityIcon fontSize="small" />, null, null, !canAccessModule("COMMUNITY_PRIORITIES", "read")),
		getItem('Reviews', '/reviews', <RateReviewIcon fontSize="small" />, null, null, !canAccessModule("REVIEWS", "read")),
		getItem('Consents', '/consents', <FactCheckIcon fontSize="small" />, null, null, !canAccessModule("CONSENTS", "read")),
	].filter((item) => item.disabled === false);

	const items = [
		getItem('Home', '/home', <HomeIcon />, 'Home'),
		getItem('Lessons', '/lessons', <BookIcon />, 'Lessons', null, !canAccessModule("LESSONS", "read")),
		getItem('Quizzes', '/quizzes', <QuestionAnswerIcon />, 'Quizzes', null, !canAccessModule("QUIZZES", "read")),
		getItem('Tasks', '/tasks', <AssignmentIcon />, 'Tasks', null, !canAccessModule("TASKS", "read")),
		getItem('Surveys', '/surveys', <NoteAddIcon />, 'Surveys', null, !canAccessModule("SURVEYS", "read")),
		getItem('Success Stories', '/stories', <CheckCircleIcon />, 'Success Stories', null, !canAccessModule("SUCCESS_STORIES", "read")),
		getItem('Groups', '/groups', <GroupsIcon />, 'Groups', null, !canAccessModule("GROUPS", "read")),
		getItem('Engagement', '/feedback-menu', <EngagementIcon />, 'Engagement', feedArray, feedArray.length === 0),
		getItem('Users', '/users', <PeopleIcon />, 'Users', null, !canAccessModule("USERS", "read")),
		getItem('Admin Configurations', '/settings', <SettingsIcon />, 'Admin Configurations', null, !canAccessModule("ADMIN_CONFIGS", "read")),
		getItem('Logs', '/logs', <HistoryIcon />, 'Logs', null, !canAccessModule("LOGS", "read")),
	];

	const handleClick = (item) => {
		if (item.children) {
			setFeedbackOpen((prev) => !prev);
		} else {
			navigate(item.key);
		}
	};

	return (
		<Box sx={{ borderRadius: 10 }}>
			<List sx={{ width: "100%", py: 0.5 }}>
				{items?.filter((list) => list.disabled === false).map((item) => {
					const isSelected = location.pathname.includes(item.key);
					return (
						<React.Fragment key={item.key}>
							<ListItem disablePadding>
								<ListItemButton
									onClick={() => handleClick(item)}
									selected={isSelected}
									sx={{
										minHeight: props.checked ? 48 : 52,
										justifyContent: props.checked ? 'flex-start' : 'center',
										gap: props.checked ? 1 : 0,
										px: props.checked ? 2 : 0,
										py: props.checked ? 1 : 0.75,
										backgroundColor: isSelected ? '#e0f7fa' : 'transparent',
										'&:hover': {
											backgroundColor: isSelected ? '#b2ebf2' : '#f0f0f0',
										},
									}}
								>
									<Tooltip title={item.tooltip} placement="right" arrow>
										<ListItemIcon
											sx={{
												color: "#51195D",
												justifyContent: 'center',
												minWidth: props.checked ? 40 : 0,
												mr: props.checked ? 0 : 0,
											}}
										>
											{item.icon}
										</ListItemIcon>
									</Tooltip>
									<ListItemText
										primary={<Typography className="text-[#51195D] font-semibold" variant="body1">{item.label}</Typography>}
										sx={{
											display: props.checked ? 'block' : 'none',
											minWidth: 0,
											opacity: props.checked ? 1 : 0,
											transition: 'opacity 0.3s',
											'& .MuiListItemText-primary': {
												whiteSpace: 'normal',
											},
										}}
									/>
									{item.children && props.checked ? (feedbackOpen ? <ExpandLessOutlined sx={{ color: "#51195D" }} /> : <ExpandMoreOutlined sx={{ color: "#51195D" }} />) : null}
								</ListItemButton>
							</ListItem>
							{item.children && props.checked && (
								<Collapse in={feedbackOpen} timeout="auto" unmountOnExit>
									<List component="div" disablePadding>
										{item.children.map((child) => (
											<ListItem key={child.key} disablePadding>
												<ListItemButton
													sx={{
														pl: props.checked ? 6 : 2,
														pr: 1,
														gap: 1.5,
														alignItems: 'center',
													}}
													onClick={() => navigate(child.key)}
													selected={location.pathname.includes(child.key)}
												>
													<ListItemIcon sx={{ color: "#51195D", minWidth: 32, justifyContent: 'center' }}>
														{child.icon}
													</ListItemIcon>
													<ListItemText
														primary={
															<Typography
																className="text-[#51195D]"
																variant="body1"
																style={{
																	whiteSpace: "normal",
																	lineHeight: 1.25,
																	overflowWrap: "break-word",
																}}
															>
																{child.label}
															</Typography>
														}
														sx={{
															minWidth: 0,
															margin: 0,
															opacity: !props.checked ? 0 : 1,
														}}
													/>
												</ListItemButton>
											</ListItem>
										))}
									</List>
								</Collapse>
							)}
						</React.Fragment>
					);
				})}
			</List>
		</Box>
	);
};

export default ProSideBar;
